/**
 * Post-authentication router (FR-RBAC-01).
 *
 * Reads the `tid` and `persona` claims from the access token and dispatches:
 *   Platform Admin           → /admin
 *   Team Admin               → /app/{slug}
 *   OC / DC                  → /app/{slug}/coaches-highlight?unit=offense|defense
 *   Coach                    → /app/{slug}/game-summary
 *   Primary/General operator → /app/{slug}/play-entry
 *   Fanatic                  → /app/fanatic-portal
 *
 * Admin personas additionally confirm the tenant is present and not soft-deleted
 * via `GET /tenants/v1/{tenant_id}`; that endpoint is admin-only, so coaching
 * and operator personas skip the check rather than eat a guaranteed 403.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '../lib/constants';
import { defaultViewLabel, resolvePersonaRoute } from '../lib/personas';

import { useApiClient } from './providers';
import { useSession } from './session-context';

const ADMIN_PERSONAS = new Set(['platform_admin', 'team_admin']);

export function TenantRouter(): JSX.Element {
  const { claims, session, persona, tenantSlug, isMfaPending, isBootstrapping } = useSession();
  const apiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const tenantId = claims?.tid ?? session?.tenantId ?? null;
  const shouldVerifyTenant =
    persona !== null && ADMIN_PERSONAS.has(persona) && tenantId !== null && !isMfaPending;

  useEffect(() => {
    if (!shouldVerifyTenant || tenantId === null) return;
    let isCancelled = false;
    setIsChecking(true);
    apiClient
      .getTenant(tenantId)
      .then((tenant) => {
        if (isCancelled) return;
        if (tenant.deleted_at) {
          setTenantError(
            'This team portal has been closed. Contact support if that is unexpected.'
          );
        }
      })
      .catch(() => {
        // Non-fatal: the portal still loads and each view reports its own errors.
      })
      .finally(() => {
        if (!isCancelled) setIsChecking(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [apiClient, shouldVerifyTenant, tenantId]);

  if (isBootstrapping) return <RouterMessage title="Restoring your session…" />;
  if (session === null || persona === null) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (isMfaPending) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (tenantError !== null) {
    return <RouterMessage title="Portal unavailable" detail={tenantError} hasSignInLink />;
  }
  if (isChecking) return <RouterMessage title="Checking your team portal…" />;

  const slug = tenantSlug ?? searchParams.get('team');
  const destination = resolvePersonaRoute(persona, slug);
  if (destination === ROUTES.login) {
    return (
      <RouterMessage
        title="We need your team code"
        detail={`Your account signs in to a specific team portal. Enter the team code to open ${defaultViewLabel(persona)}.`}
        hasSignInLink
      />
    );
  }
  return <Navigate to={destination} replace />;
}

function RouterMessage({
  title,
  detail,
  hasSignInLink,
}: {
  title: string;
  detail?: string;
  hasSignInLink?: boolean;
}): JSX.Element {
  return (
    <div className="auth">
      <div className="auth__panel">
        <h1 className="auth__title">{title}</h1>
        {detail ? <p className="muted">{detail}</p> : null}
        {hasSignInLink ? (
          <Link className="btn btn--primary btn--block" to={ROUTES.login}>
            Go to sign in
          </Link>
        ) : null}
      </div>
    </div>
  );
}
